import type { $Values } from 'utility-types'

import { NotifiableTypes, RelationTypes } from 'modules/automations/lib/accessGroup'
import { AccessGroup } from 'modules/automations/store/models/AccessGroup'

export const RecipientTypes = {
  user: 'user',
  group: 'group',
  manualSelection: 'manual_selection',
}

export const Recipients = { ...NotifiableTypes, ...RelationTypes }

export type RecipientType = $Values<typeof RecipientTypes>

export type Recipient = $Values<typeof Recipients>

export interface Payload {
  relation_type: string | null | undefined
  notifiable_type?: string | null | undefined
  notifiable_id?: number | null | undefined
  notifiable_ids?: number[] | null | undefined
  automations_action_id: number | null | undefined
}

const isUser = (str: string): boolean => {
  return [
    RelationTypes.self,
    RelationTypes.manager,
    RelationTypes.timeoff_manager,
    RelationTypes.employeesTeamLead,
  ].includes(str)
}

export const accessGroupToRecipientType = (
  accessGroup: AccessGroup | null | undefined
): RecipientType | null => {
  if (!accessGroup) return null

  if (!accessGroup.attributes.notifiable_type) {
    if (isUser(accessGroup.attributes.relation_type)) {
      return RecipientTypes.user
    }

    return RecipientTypes.group
  }

  switch (accessGroup.attributes.notifiable_type) {
    case NotifiableTypes.access:
      return RecipientTypes.manualSelection
    default:
      return RecipientTypes.group
  }
}

export const accessGroupToRecipient = (
  accessGroup: AccessGroup | null | undefined
): Recipient | null => {
  if (!accessGroup) return null

  if (accessGroup.attributes.relation_type === RelationTypes.notifiable) {
    return accessGroup.attributes.notifiable_type
  }

  return accessGroup.attributes.relation_type
}

export interface Data {
  recipient: string
  notifiable_id: number | null | undefined
  notifiable_ids?: number[] | null | undefined
  automations_action_id: number | null | undefined
}

export const buildAccessGroupPayload = ({
  recipient,
  notifiable_id,
  notifiable_ids,
  // eslint-disable-line camelcase
  automations_action_id, // eslint-disable-line camelcase
}: Data): Payload => {
  if (isNotifiable(recipient)) {
    // NOTE: This might become a switch statement as more recipient types
    // require tweaked versions of the payload. Is the price we pay for having
    // forms that do not match 1to1 with our data model.
    if (recipient === Recipients.teamLead) {
      return {
        notifiable_id,
        automations_action_id,
        notifiable_type: NotifiableTypes.team,
        relation_type: RelationTypes.teamLead,
      }
    }

    // when updating using multiple selection with notifiable_ids, we need to set notifiable_id as null
    // keep notifiable_id when updating using single selection for other email types
    return {
      notifiable_id:
        recipient === Recipients.access && notifiable_id && notifiable_ids?.length
          ? null
          : notifiable_id,
      notifiable_ids,
      automations_action_id,
      notifiable_type: recipient,
      relation_type: RelationTypes.notifiable,
    }
  }

  return {
    automations_action_id,
    relation_type: recipient,
  }
}

export const isNotifiable = (recipient: string): boolean => {
  return recipient === Recipients.teamLead || Object.values(NotifiableTypes).includes(recipient)
}
